import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { Camera, ScanLine, X, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCustomerAuth, API } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';

/**
 * OrderLookupCard — scan or type an invoice/order number to jump to
 * the public order-tracking page.
 *
 * Two input modes:
 *  1) **Manual entry** — text field + Look up button. Accepts any
 *     order number; we normalize to uppercase + trim whitespace so
 *     "mr-00000077" works the same as "MR-00000077".
 *  2) **Camera barcode scan** — uses the device's back camera (or
 *     whatever camera the browser picks) and zxing-browser to decode
 *     Code-39 barcodes printed on invoice PDFs. Streams the result
 *     into the text field so the user can review before jumping.
 *
 * Why the public `/track/:orderNumber` route (instead of the
 * authenticated `/api/customer/orders/:id`): the order tracking page
 * works for both signed-in and walk-up users, and an invoice barcode
 * may be scanned by someone other than the original purchaser
 * (warehouse, partner, etc.). The track endpoint is rate-limited
 * server-side and only exposes non-sensitive fields.
 */
const OrderLookupCard = () => {
  const { authHeaders } = useCustomerAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [value, setValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const controlsRef = useRef(null);

  // --- Camera scan lifecycle ---
  useEffect(() => {
    if (!scanning) return undefined;

    let cancelled = false;
    // Restrict zxing to formats we actually print on invoices to cut
    // false positives. Code-39 is what the backend renders; we also
    // accept Code-128 and QR in case warehouses use different label
    // printers down the line.
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_128,
      BarcodeFormat.QR_CODE,
    ]);
    const reader = new BrowserMultiFormatReader(hints);

    const start = async () => {
      try {
        // Prefer the rear-facing camera on phones — `environment` is the
        // MediaStreamConstraints magic string for the back camera.
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result) => {
            if (!result || cancelled) return;
            const text = (result.getText() || '').trim().toUpperCase();
            if (!text) return;
            setValue(text);
            stopScan();
            // Tiny delay so the toast renders before navigation kicks in.
            setTimeout(() => lookup(text), 150);
          },
        );
        controlsRef.current = controls;
      } catch (err) {
        if (cancelled) return;
        setScanError(
          err?.name === 'NotAllowedError'
            ? 'Camera access blocked. Enable camera permission in your browser settings.'
            : 'Could not start the camera. Use manual entry instead.',
        );
      }
    };
    start();

    return () => {
      cancelled = true;
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const stopScan = () => {
    try {
      controlsRef.current?.stop?.();
    } catch { /* ignore */ }
    controlsRef.current = null;
    setScanning(false);
  };

  const lookup = async (raw) => {
    const orderNumber = (raw ?? value).trim().toUpperCase();
    if (!orderNumber) {
      toast({ title: 'Enter an order number', description: 'e.g. MR-00000077' });
      return;
    }
    setBusy(true);
    try {
      // Public track endpoint accepts any order number; rejects with 404
      // if the order doesn't exist. We always navigate the user via the
      // public /track route (not /my-orders deep-link) so the lookup
      // works for invoices that may have been issued to someone else
      // on the same device (e.g., admin / warehouse staff).
      const { data } = await axios.get(`${API}/track/${encodeURIComponent(orderNumber)}`, {
        headers: authHeaders,
      });
      if (data?.order_number) {
        navigate(`/track/${data.order_number}`);
      } else {
        toast({ title: 'Order not found', description: orderNumber });
      }
    } catch (err) {
      const status = err?.response?.status;
      toast({
        title: status === 404 ? 'Order not found' : 'Lookup failed',
        description: status === 404 ? orderNumber : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 mb-6"
      data-testid="order-lookup-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] text-[#0078D4] uppercase">
            Find an order
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900 leading-tight mt-0.5">
            Scan invoice barcode or type the order number
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Format <span className="font-mono">MR-########</span> · works with printed invoices.
          </p>
        </div>
      </div>

      {scanning ? (
        <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3] mb-3" data-testid="order-lookup-scanner">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />
          {/* Scan target overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-3/4 h-20 border-2 border-emerald-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
          </div>
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
            <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold text-white inline-flex items-center gap-1.5">
              <ScanLine className="w-3 h-3 animate-pulse" /> Point at barcode
            </div>
            <button
              type="button"
              onClick={stopScan}
              className="bg-white/90 hover:bg-white text-slate-900 rounded-full p-1.5 transition"
              data-testid="order-lookup-scanner-close"
              aria-label="Stop scanning"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {scanError && (
            <div className="absolute inset-x-3 bottom-3 bg-red-500/95 text-white text-xs font-semibold rounded-lg p-2 flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {scanError}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
            placeholder="MR-00000077"
            className="flex-1 min-w-0 rounded-full border border-slate-300 px-4 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
            data-testid="order-lookup-input"
            autoComplete="off"
            spellCheck="false"
            inputMode="text"
          />
          <button
            type="button"
            onClick={() => { setScanError(null); setScanning(true); }}
            className="rounded-full border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 inline-flex items-center gap-1.5 transition"
            data-testid="order-lookup-scan-btn"
            aria-label="Scan barcode"
          >
            <Camera className="w-4 h-4" /> Scan
          </button>
          <button
            type="button"
            onClick={() => lookup()}
            disabled={busy || !value.trim()}
            className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:bg-[#0078D4]/40 disabled:cursor-not-allowed text-white text-xs font-bold px-4 py-2 inline-flex items-center gap-1.5 transition"
            data-testid="order-lookup-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Look up
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderLookupCard;
